import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail, API } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { CourseFormModal } from "../components/CourseFormModal";
import { AuthImage } from "../components/AuthImage";
import { IndianRupee, Users, BookOpen, Clock, Plus, Pencil, Trash2, Check, X, Eye, Settings, UploadCloud, QrCode, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewShot, setViewShot] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [upiId, setUpiId] = useState("");
  const [payeeName, setPayeeName] = useState("");
  const [qrPath, setQrPath] = useState("");
  const [qrPreview, setQrPreview] = useState("");
  const [qrUploading, setQrUploading] = useState(false);
  const qrRef = useRef();

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/courses").then((r) => setCourses(r.data));
    api.get("/admin/payments").then((r) => setPayments(r.data));
    api.get("/upi-config").then((r) => { setUpiId(r.data.upi_id); setPayeeName(r.data.payee_name); setQrPath(r.data.qr_path || ""); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteCourse = async (id) => {
    if (!window.confirm("Delete this course?")) return;
    await api.delete(`/admin/courses/${id}`);
    toast.success("Course deleted");
    load();
  };

  const approve = async (id) => {
    await api.post(`/admin/payments/${id}/approve`);
    toast.success("Payment approved & student enrolled");
    load();
  };

  const doReject = async () => {
    await api.post(`/admin/payments/${rejecting.id}/reject`, { reason });
    toast.success("Payment rejected");
    setRejecting(null); setReason(""); load();
  };

  const handleQrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setQrPreview(URL.createObjectURL(file));
    setQrUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setQrPath(data.path);
      toast.success("QR uploaded — click Save Settings to apply");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setQrUploading(false); }
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", { upi_id: upiId, payee_name: payeeName, qr_path: qrPath });
      toast.success("UPI settings updated");
      setQrPreview("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const statCards = stats ? [
    { icon: IndianRupee, label: "Revenue", value: `₹${stats.revenue}`, color: "text-emerald-600 bg-emerald-50" },
    { icon: Clock, label: "Pending Approvals", value: stats.pending_approvals, color: "text-amber-600 bg-amber-50" },
    { icon: Users, label: "Students", value: stats.total_students, color: "text-indigo-600 bg-indigo-50" },
    { icon: BookOpen, label: "Courses", value: stats.total_courses, color: "text-teal-700 bg-teal-50" },
  ] : [];

  return (
    <div className="min-h-screen bg-[#FAFAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Admin Panel</h1>
        <p className="text-slate-500 mt-1">Manage courses, approve payments and configure UPI.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {statCards.map((s) => (
            <div key={s.label} className="rounded-2xl bg-white border border-slate-200 p-5">
              <span className={`grid place-items-center h-10 w-10 rounded-xl ${s.color} mb-3`}><s.icon className="h-5 w-5" /></span>
              <p className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{s.value}</p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="payments" className="mt-10">
          <TabsList>
            <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="courses" data-testid="admin-tab-courses">Courses</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">UPI Settings</TabsTrigger>
          </TabsList>

          {/* Payments */}
          <TabsContent value="payments" className="mt-6">
            {payments.length === 0 ? (
              <p className="text-center text-slate-500 py-16 rounded-2xl bg-white border border-slate-200">No payments submitted yet.</p>
            ) : (
              <div className="rounded-2xl bg-white border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="px-5 py-3 font-medium">Student</th>
                      <th className="px-5 py-3 font-medium">Course</th>
                      <th className="px-5 py-3 font-medium">Amount</th>
                      <th className="px-5 py-3 font-medium">UTR</th>
                      <th className="px-5 py-3 font-medium">Proof</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 font-medium text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {payments.map((p) => (
                      <tr key={p.id} data-testid={`admin-payment-${p.id}`}>
                        <td className="px-5 py-3.5"><div className="font-medium text-slate-800">{p.user_name}</div><div className="text-xs text-slate-400">{p.user_email}</div></td>
                        <td className="px-5 py-3.5 text-slate-700">{p.course_title}</td>
                        <td className="px-5 py-3.5 font-semibold">₹{p.amount}</td>
                        <td className="px-5 py-3.5 font-mono text-xs">{p.utr}</td>
                        <td className="px-5 py-3.5">
                          {p.screenshot_path ? (
                            <button onClick={() => setViewShot(p.screenshot_path)} className="text-teal-700 hover:text-teal-600 flex items-center gap-1 text-xs font-medium"><Eye className="h-4 w-4" /> View</button>
                          ) : "—"}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${p.status === "approved" ? "bg-emerald-100 text-emerald-700" : p.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          {p.status === "pending" && (
                            <div className="flex items-center justify-end gap-2">
                              <Button data-testid="admin-payment-approve-button" size="sm" onClick={() => approve(p.id)} className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 px-3"><Check className="h-4 w-4" /></Button>
                              <Button data-testid="admin-payment-reject-button" size="sm" variant="outline" onClick={() => setRejecting(p)} className="h-8 px-3 border-rose-300 text-rose-600"><X className="h-4 w-4" /></Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* Courses */}
          <TabsContent value="courses" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button data-testid="admin-add-course-button" onClick={() => { setEditing(null); setShowForm(true); }} className="bg-teal-700 hover:bg-teal-600 text-white"><Plus className="h-4 w-4 mr-1.5" /> Add Course</Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {courses.map((c) => (
                <div key={c.id} className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
                  <img src={c.cover_image} alt={c.title} className="h-32 w-full object-cover" />
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.is_free ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{c.is_free ? "FREE" : `₹${c.price}`}</span>
                      <span className="text-xs text-slate-400">{c.category}</span>
                    </div>
                    <h3 className="font-semibold text-slate-900 text-sm">{c.title}</h3>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => { setEditing(c); setShowForm(true); }} className="flex-1"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => deleteCourse(c.id)} className="border-rose-300 text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings" className="mt-6">
            <div className="rounded-2xl bg-white border border-slate-200 p-6 max-w-md">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}><Settings className="h-4 w-4 text-teal-700" /> UPI Payment Settings</h3>
              <div className="space-y-4">
                <div><Label>UPI ID</Label><Input data-testid="settings-upi-id" value={upiId} onChange={(e) => setUpiId(e.target.value)} className="mt-1.5" placeholder="yourname@upi" /></div>
                <div><Label>Payee Name</Label><Input data-testid="settings-payee-name" value={payeeName} onChange={(e) => setPayeeName(e.target.value)} className="mt-1.5" /></div>
                <div>
                  <Label className="flex items-center gap-1.5"><QrCode className="h-4 w-4 text-teal-700" /> Payment QR Code</Label>
                  <p className="text-xs text-slate-400 mt-1">Upload your UPI QR image. Students scan this exact QR to pay.</p>
                  <input ref={qrRef} data-testid="settings-qr-upload" type="file" accept="image/*" onChange={handleQrUpload} className="hidden" />
                  <div className="mt-2 flex items-center gap-4">
                    <div className="h-28 w-28 rounded-xl border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden shrink-0">
                      {qrPreview ? (
                        <img src={qrPreview} alt="QR preview" className="h-full w-full object-contain p-1" />
                      ) : qrPath ? (
                        <img src={`${API}/upi-qr?v=${encodeURIComponent(qrPath)}`} alt="Current QR" className="h-full w-full object-contain p-1" />
                      ) : (
                        <QrCode className="h-8 w-8 text-slate-300" />
                      )}
                    </div>
                    <Button type="button" variant="outline" onClick={() => qrRef.current?.click()} disabled={qrUploading} className="gap-1.5">
                      {qrUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} {qrPath ? "Replace QR" : "Upload QR"}
                    </Button>
                  </div>
                </div>
                <Button data-testid="settings-save-button" onClick={saveSettings} className="bg-teal-700 hover:bg-teal-600 text-white">Save Settings</Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {showForm && <CourseFormModal open={showForm} onClose={() => setShowForm(false)} initial={editing} onSaved={load} />}

      {/* Screenshot viewer */}
      <Dialog open={!!viewShot} onOpenChange={(v) => !v && setViewShot(null)}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Payment Screenshot</DialogTitle></DialogHeader>
          {viewShot && <AuthImage path={viewShot} alt="proof" className="w-full rounded-xl max-h-[70vh] object-contain" />}
        </DialogContent>
      </Dialog>

      {/* Reject modal */}
      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent><DialogHeader><DialogTitle>Reject Payment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Reason (shown to student)</Label><Input data-testid="reject-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" placeholder="e.g. UTR not found / amount mismatch" /></div>
            <Button data-testid="reject-confirm-button" onClick={doReject} className="w-full bg-rose-600 hover:bg-rose-500 text-white">Confirm Reject</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
